import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import type { createDoctorSchema } from '@virtual-care/client';
import { phoneValidator } from '@virtual-care/isomorphic';
import { Button, DialogFooter, Input, Label } from '@virtual-care/shadcn';
import {
  BirthDateField,
  FieldMessage,
  FormMessage,
  GenderField,
  client,
  errorToFormIssue,
} from '@virtual-care/ui';
import { CreateDialog } from '../../components/create-dialog.tsx';
import { GeneratedPassword } from '../../components/generated-password.tsx';
import { PhoneField } from '../../components/phone-field.tsx';
import { SpecialitySelect } from '../../components/speciality-select.tsx';

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email format' }),
  phoneNumber: z.string(),
  scfhsNo: z.string().min(1, { message: 'SCFHS No is required' }),
  specialityId: z.string().min(1, { message: 'Speciality is required' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  gender: z.enum(['male', 'female'], { message: 'Please select a gender' }),
  birthDate: z.string().min(1, { message: 'Birth date is required' }),
});

const phoneNumberValidator = schema.shape.phoneNumber.superRefine(
  phoneValidator({ validateEmpty: false }),
);

export function CreateDoctor(props: {
  trigger: React.ReactNode;
  onSave: () => void;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null,
  );

  const form = useForm({
    defaultValues: {
      email: '',
      phoneNumber: '',
      scfhsNo: '',
      specialityId: '',
      firstName: '',
      lastName: '',
      gender: 'male',
      birthDate: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          const { password } = await client.request('POST /doctors', {
            email: value.email,
            phoneNumber: value.phoneNumber,
            scfhsNo: value.scfhsNo,
            specialityId: value.specialityId,
            firstName: value.firstName,
            lastName: value.lastName,
            gender: value.gender as z.infer<
              typeof createDoctorSchema.shape.gender
            >,
            birthDate: value.birthDate,
          });
          setGeneratedPassword(password);
          toast.success('Doctor created successfully');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={props.open}
      onOpenChange={props.onOpenChange}
      trigger={props.trigger}
      title="Create Doctor"
      subtitle="Add a new doctor"
      onSave={props.onSave}
    >
      <form.Field
        name="email"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="doctor@hospital.com"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="firstName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="John"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="lastName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Doe"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <div className="flex gap-x-2">
        <form.Field
          name="gender"
          children={(field) => (
            <GenderField
              field={field}
              label="Gender"
              placeholder="Select gender"
              maleLabel="Male"
              femaleLabel="Female"
            />
          )}
        />

        <form.Field
          name="birthDate"
          children={(field) => (
            <BirthDateField className="flex-1" label="Birth Date" placeholder="Pick a date" field={field} />
          )}
        />
      </div>

      <form.Field
        name="phoneNumber"
        validators={{
          onBlurAsync: phoneNumberValidator,
        }}
        children={(field) => <PhoneField field={field} />}
      />

      <form.Field
        name="scfhsNo"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="scfhsNo">SCFHS No</Label>
            <Input
              id="scfhsNo"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Enter SCFHS number"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="specialityId"
        children={(field) => <SpecialitySelect field={field} />}
      />

      {generatedPassword && (
        <GeneratedPassword generatedPassword={generatedPassword} />
      )}
      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <form.Subscribe
          selector={(state) => [state.isSubmitSuccessful]}
          children={([isSubmitSuccessful]) =>
            isSubmitSuccessful ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  props.onOpenChange(false);
                  setGeneratedPassword(null);
                  form.reset();
                }}
              >
                Close
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => props.onOpenChange(false)}
              >
                Cancel
              </Button>
            )
          }
        />
        <form.Subscribe
          selector={(state) =>
            [
              state.canSubmit,
              state.isSubmitting,
              state.isSubmitSuccessful,
            ] as const
          }
          children={([canSubmit, isSubmitting, isSubmitSuccessful]) => (
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting || isSubmitSuccessful}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
